import { Button, Container, Divider, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import AccountHeader from "../AccountHeader";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import { useParams } from "react-router";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { FetchPurchaseReportData } from "../../UserData/_actions/Store/StoreActions";
import LabLoader from "../../DesignLab/_components/LabLoader";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { CartStack } from "../../Cart/CartContent/CartStack";
import { DatePicker } from "@mui/x-date-pickers";
import PurchaseReportStore, { type IPurchaseReportStore } from "../../UserData/_stores/Store/PurchaseReportStore";
import StoreHeader from "./StoreHeader";

export default function StorePurchaseReport() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const theme = useTheme()
  const breakpointSmDown = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams<{ storeSlug: string }>();

  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('fetch_purchase_report_data'))
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang')) ?? 'en_CA'

  const [startDate, setStartDate] = useState(DateTime.now().minus({ days: 30 }))
  const [endDate, setEndDate] = useState(DateTime.now())
  const reportData = useAppSelector(state => params.storeSlug
    ? state.get('UIData').get('account').get('storePurchaseReports').get(params.storeSlug)
    : undefined
  ) ?? new PurchaseReportStore()
  const taxRows = useMemo<Array<{ key: keyof IPurchaseReportStore, label: string }>>(() => [
    { key: 'GST', label: t('GST') },
    { key: 'QST', label: t('QST') },
    { key: 'HST', label: t('HST') },
    { key: 'PST_SK', label: t('Saskatchewan PST') },
    { key: 'PST_BC', label: t('British Columbia PST') },
  ], [t])

  
  const purchaseReportPdfUrl = useMemo(() => {
    const lang = locale.substring(0, 2)
    return params.storeSlug
      ? (lang === 'en' ? '' : lang + '/') + 'api/user/stores/' + params.storeSlug + '/purchase-report-pdf?startDate=' + startDate.toFormat('yyyy/LL/dd') + '&endDate=' + endDate.toFormat('yyyy/LL/dd')
      : undefined
  }, [locale, params.storeSlug, startDate, endDate]);

  const onChangeStartDate = useCallback((date: DateTime | null) => {
    if (!date) return
    setStartDate(date)
  }, [])

  const onChangeEndDate = useCallback((date: DateTime | null) => {
    if (!date) return
    setEndDate(date)
  }, [])

  useEffect(() => {
    if (!params.storeSlug) return;

    dispatch(FetchPurchaseReportData(params.storeSlug, startDate.toFormat('yyyy/LL/dd'), endDate.toFormat('yyyy/LL/dd')))
  }, [dispatch, params.storeSlug, startDate, endDate])

  return <div>
    <StoreHeader
      title={t('Purchase report')}
    />

    <Container
      maxWidth="sm"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="flex-end">

          <DatePicker
            label={t('Start date')}
            value={startDate}
            onChange={onChangeStartDate}
          />

          <DatePicker
            label={t('End date')}
            value={endDate}
            onChange={onChangeEndDate}
          />

        </Stack>
          <Paper elevation={0} sx={{ p: 3 }}>
            {loader ? <LabLoader /> : <>
              <Stack spacing={3} divider={<Divider />}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="h3">{t('Purchase report')}</Typography>
                  <Button
                    variant="dashed"
                    color="secondary"
                    startIcon={<FileDownloadOutlinedIcon />}
                    component="a"
                    href={purchaseReportPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >{t('Download (PDF)')}</Button>
                </Stack>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="body1">{t('Start date')}</Typography>
                    <Typography variant="h6">{startDate.toLocaleString(DateTime.DATE_MED)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="body1">{t('End date')}</Typography>
                    <Typography variant="h6">{endDate.toLocaleString(DateTime.DATE_MED)}</Typography>
                  </Stack>
                </Stack>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" sx={{ color: '#999' }}>{t('Subtotal')}</Typography>
                    <Typography variant="h6">$ {reportData.get('subtotal')}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" sx={{ color: '#999' }}>{t('Discount')}</Typography>
                    <Typography variant="h6">($ {reportData.get('discount')})</Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" sx={{ color: '#999' }}>{t('Shipping')}</Typography>
                    <Typography variant="h6">$ {reportData.get('shipping')}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" sx={{ color: '#999' }}>{t('Taxes')}</Typography>
                    <Typography variant="h6">$ {reportData.get('taxes')}</Typography>
                  </Stack>
                  {taxRows.map(row => {
                    const amount = reportData.get(row.key)

                    return amount !== '0.00' ? <Stack key={row.key} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Typography variant="body1" sx={{ color: '#999' }}>{row.label}</Typography>
                      <Typography variant="h6">$ {amount}</Typography>
                    </Stack> : null
                  })}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" sx={{ color: '#999' }}>{t('TOTAL')}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>$ {reportData.get('total')}</Typography>
                </Stack>
              </Stack>
              <CartStack
                direction={{ xs: 'column', sm: 'row' }}
                divider={<Divider
                  orientation={breakpointSmDown ? 'horizontal' : 'vertical'}
                  flexItem
                />}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: {
                    xs: 'center',
                    sm: 'left'
                  }
                }}
                spacing={1}
              >
                <Typography variant="caption">
                  Art of Where<br />
                  5534 Ferrier<br />
                  Mont-Royal, QC H4P 1M2, Canada
                </Typography>
                <Typography variant="caption">
                  GST: 831650338 RT 0001
                  <br />QST: 1219943063 TQ 0001
                  <br />PST (SK): 7395783
                  <br />PST (BC): 1443-9371
                  <br />VAT (UK): 381 4991 65
                </Typography>
              </CartStack>
            </>}
          </Paper>
      </Stack>
    </Container>
  </div >
}
